/** @typedef {import("../../../api/types").Order} Order */
import React from "react";
import {Link} from "react-router-dom";
import {Spinner, Table} from "reactstrap";
import {formatDate, formatMoney} from "../../../shared/utils/format";
import ClickableRow from "../../../shared/components/ClickableRow";
import OrderStatusBadge, {STATUS_STYLES} from "./OrderStatusBadge";
import SortableTh from "../../../shared/components/SortableTh";
import "./OrdersTable.css";

const COLUMNS = 6;

// On a phone only #, client and total stay (see OrdersTable.css): the status is
// only a stripe on the row's edge.
const WIDE = "d-none d-md-table-cell";

/**
 * The orders table, shared by the orders list and the home page's recent
 * orders so both show the same columns.
 *
 * @param {{
 *   orders: Order[]|null,
 *   emptyMessage?: string,
 *   className?: string,
 *   sort?: {ordering: string, onChange: (ordering: string) => void},
 * }} props `orders` is null while loading; `emptyMessage` is shown when
 * there are none (leave it out to show nothing, e.g. after an error). With
 * `sort`, the "#" and "Total" headers become sort buttons; `ordering` is the
 * API's value ("number", "-total_value", ...).
 */
const OrdersTable = ({orders, emptyMessage, className, sort}) => (
    <Table hover responsive className={className}>
        <thead>
            <tr>
                <SortableTh field="number" sort={sort}>
                    #
                </SortableTh>
                <th className={WIDE}>Situação</th>
                <th>Cliente</th>
                <th className={WIDE} style={{width: "35%"}}>
                    Observações
                </th>
                <th className={WIDE}>Criada em</th>
                <SortableTh field="total_value" sort={sort} className="text-end">
                    Total
                </SortableTh>
            </tr>
        </thead>
        <tbody>
            {orders === null && (
                <tr>
                    <td colSpan={COLUMNS} className="text-center">
                        <Spinner size="sm" />
                    </td>
                </tr>
            )}
            {orders?.length === 0 && emptyMessage && (
                <tr>
                    <td colSpan={COLUMNS} className="text-center text-muted">
                        {emptyMessage}
                    </td>
                </tr>
            )}
            {orders?.map((order) => (
                <ClickableRow
                    key={order.id}
                    to={`/orders/${order.id}`}
                    className={`order-row order-row--${(STATUS_STYLES[order.status] ?? {color: "secondary"}).color}`}
                >
                    <td>
                        <Link to={`/orders/${order.id}`}>{order.number}</Link>
                    </td>
                    <td className={WIDE}>
                        <OrderStatusBadge status={order.status} />
                    </td>
                    <td>{order.client?.name}</td>
                    {/* maxWidth 0 lets the column shrink so the text can be cut with an ellipsis. */}
                    <td
                        className={`${WIDE} text-truncate`}
                        style={{maxWidth: 0}}
                        title={order.observations ?? undefined}
                    >
                        {order.observations || <span className="text-muted">—</span>}
                    </td>
                    <td className={WIDE}>{formatDate(order.created_at)}</td>
                    <td className="order-total text-end">{formatMoney(order.total_value, order.currency)}</td>
                </ClickableRow>
            ))}
        </tbody>
    </Table>
);

export default OrdersTable;
